import { ChevronLeft, ChevronRight, Search, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Avatar,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@/components/ui';
import { Select } from '@/components/ui/select-menu';
import useClampPage from '@/hooks/useClampPage';
import useDebounce from '@/hooks/useDebounce';
import usePersistedState from '@/hooks/usePersistedState';
import { useDeleteUser, useUsers } from '@/hooks/useUsers';
import { useAppSelector } from '@/store';
import { ApprovalStatus, User } from '@/types';
import cn from '@/utils/cn';
import { PAGINATION, ROUTES } from '@/utils/constants';
import { formatDate, formatDateTime } from '@/utils/format';

const statusVariant: Record<ApprovalStatus, 'success' | 'warning' | 'danger'> = {
  approved: 'success',
  pending: 'warning',
  rejected: 'danger',
};

// The four roles Request Access offers, mirroring the server's
// REQUESTABLE_JOB_ROLES. 'Renewal' and 'Super Admin' are deliberately absent:
// Renewals is worked by the Billing desk and there is one admin level, so
// neither can be requested any more (ADR-086).
//
// TRADE-OFF: an account registered under the old list still HOLDS one of those
// values, and this filter can no longer select for it. Such a user still appears
// in the unfiltered list with their real job role shown in the table, so nobody
// becomes invisible - they just cannot be isolated by this dropdown.
const JOB_ROLE_OPTIONS = ['Sales RM', 'Billing', 'Service', 'Admin'];

const columns = [
  'User',
  'Mobile',
  'Role',
  'Job Role',
  'Designation',
  'DOJ',
  'Status',
  'Activity',
  '',
];

const ALL_LIMIT = PAGINATION.MAX_LIMIT;
const PAGE_SIZES = [10, 25, 50, ALL_LIMIT];

const TH =
  'whitespace-nowrap px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-ink/40';
const TD = 'whitespace-nowrap px-5 py-3.5 text-sm';

const AdminUsersPage = () => {
  const navigate = useNavigate();
  // View state persists across refresh and navigation (e.g. opening a user to
  // set targets and coming back) via sessionStorage. See usePersistedState.
  const [page, setPage] = usePersistedState<number>('adminUsers:page', PAGINATION.DEFAULT_PAGE);
  const [limit, setLimit] = usePersistedState<number>('adminUsers:limit', PAGINATION.DEFAULT_LIMIT);
  const [searchInput, setSearchInput] = usePersistedState('adminUsers:search', '');
  const search = useDebounce(searchInput, 350);

  const [roleFilter, setRoleFilter] = usePersistedState('adminUsers:role', '');
  const [jobRoleFilter, setJobRoleFilter] = usePersistedState('adminUsers:jobRole', '');
  const [designationInput, setDesignationInput] = usePersistedState('adminUsers:designation', '');
  const designationFilter = useDebounce(designationInput, 350);

  const { toast } = useToast();
  const currentUserId = useAppSelector((state) => state.auth.user?.id);
  const deleteUser = useDeleteUser();
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);

  // Reset to page 1 only when a filter actually changes, not on the initial
  // mount - otherwise the restored page would be clobbered back to 1.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setPage(PAGINATION.DEFAULT_PAGE);
  }, [search, limit, roleFilter, jobRoleFilter, designationFilter, setPage]);

  // Never request page < 1 (fails the server's page.min(1) validator); guards the
  // render before useClampPage repairs a stale page=0 from sessionStorage.
  const safePage = Math.max(1, page);

  const { data, isLoading, isError, isFetching } = useUsers({
    page: safePage,
    limit,
    search: search.trim() || undefined,
    role: (roleFilter as 'user' | 'admin') || undefined,
    jobRole: jobRoleFilter || undefined,
    designation: designationFilter.trim() || undefined,
  });

  const onConfirmDelete = (): void => {
    if (!deleteTarget) return;
    deleteUser.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(`Deleted ${deleteTarget.name} and all their related records`);
        setDeleteTarget(null);
      },
      onError: () => toast.error('Could not delete that user. Please try again.'),
    });
  };

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  // A persisted page can outrun the list after it shrinks; pull it back in range.
  useClampPage(page, totalPages, !isLoading, setPage);
  const rangeStart = total === 0 ? 0 : (safePage - 1) * limit + 1;
  const rangeEnd = limit === ALL_LIMIT ? total : Math.min(safePage * limit, total);

  const renderBody = () => {
    if (isLoading) {
      return Array.from({ length: 6 }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <tr key={i}>
          <td colSpan={columns.length} className="px-5 py-3.5">
            <div className="h-4 w-full max-w-md animate-pulse rounded bg-line/50" aria-hidden />
            <span className="sr-only">Loading</span>
          </td>
        </tr>
      ));
    }

    if (isError) {
      return (
        <tr>
          <td colSpan={columns.length} className="px-5 py-12 text-center text-sm text-danger">
            Failed to load users. Please try again.
          </td>
        </tr>
      );
    }

    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan={columns.length} className="px-5 py-12 text-center text-sm text-ink/50">
            No users match the current filters.
          </td>
        </tr>
      );
    }

    return rows.map((user) => (
      <tr
        key={user.id}
        className="cursor-pointer transition-colors hover:bg-cream/50"
        onClick={() => {
          void navigate(ROUTES.ADMIN_USER_DETAIL(user.id));
        }}
      >
        <td className="px-5 py-3.5">
          <div className="flex items-center gap-3">
            <Avatar name={user.name} size="sm" />
            <div className="min-w-0 max-w-[15rem]">
              <div className="truncate text-sm font-semibold text-ink">{user.name}</div>
              <div className="truncate text-xs text-ink/55">{user.email ?? '-'}</div>
            </div>
          </div>
        </td>
        <td className={`${TD} text-ink/60`}>+91 {user.mobile}</td>
        <td className="px-5 py-3.5">
          <Badge variant={user.role === 'admin' ? 'info' : 'neutral'} className="capitalize">
            {user.role}
          </Badge>
        </td>
        <td className={`${TD} text-ink/70`}>{user.jobRole ?? '-'}</td>
        <td className={`${TD} text-ink/70`}>
          {[user.designationLevel, user.designationDept].filter(Boolean).join(' · ') || '-'}
        </td>
        <td className={`${TD} text-ink/60`}>
          {user.dateOfJoining ? formatDate(user.dateOfJoining) : '-'}
        </td>
        <td className="px-5 py-3.5">
          <Badge variant={statusVariant[user.approvalStatus]} className="capitalize">
            {user.approvalStatus}
          </Badge>
        </td>
        <td className={`${TD} text-ink/70`}>
          <div>{user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never'}</div>
          <div className="text-xs text-ink/45">Joined {formatDate(user.createdAt)}</div>
        </td>
        <td className={TD} onClick={(e) => e.stopPropagation()}>
          {user.id === currentUserId ? (
            <span className="text-xs text-ink/40">That&apos;s you</span>
          ) : (
            <button
              type="button"
              onClick={() => setDeleteTarget(user)}
              className="flex items-center gap-1.5 font-semibold text-danger transition-colors hover:text-danger-hover active:text-danger-hover"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          )}
        </td>
      </tr>
    ));
  };

  return (
    <div>
      <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">All users</h1>
      <p className="mt-1.5 text-sm text-ink/60">
        Click any row to view and edit the employee&apos;s full profile.
      </p>

      <div className="mt-6 flex flex-col gap-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1 lg:max-w-md">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name, mobile, or email…"
              className="w-full rounded-xl border border-line bg-white py-2.5 pl-10 pr-3 text-sm text-ink shadow-sm placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
          </div>

          <div className="relative lg:max-w-[12rem] flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
            <input
              type="text"
              value={designationInput}
              onChange={(e) => setDesignationInput(e.target.value)}
              placeholder="Filter by designation…"
              className="w-full rounded-xl border border-line bg-white py-2.5 pl-10 pr-3 text-sm text-ink shadow-sm placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Select
            aria-label="Filter by role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="w-40"
          >
            <option value="">All roles</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </Select>

          <Select
            aria-label="Filter by job role"
            value={jobRoleFilter}
            onChange={(e) => setJobRoleFilter(e.target.value)}
            className="w-44"
          >
            <option value="">All job roles</option>
            {JOB_ROLE_OPTIONS.map((jr) => (
              <option key={jr} value={jr}>
                {jr}
              </option>
            ))}
          </Select>

          {(roleFilter || jobRoleFilter || designationInput) && (
            <button
              type="button"
              onClick={() => {
                setRoleFilter('');
                setJobRoleFilter('');
                setDesignationInput('');
              }}
              className="text-sm font-semibold text-ink/50 transition-colors hover:text-ink"
            >
              Clear filters
            </button>
          )}

          <div className="ml-auto flex items-center gap-4">
            <span className="flex items-center gap-2 text-sm text-ink/60">
              Rows per page
              <Select
                aria-label="Rows per page"
                value={String(limit)}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="w-24"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size === ALL_LIMIT ? 'All' : size}
                  </option>
                ))}
              </Select>
            </span>

            <div className="flex items-center gap-3">
              <span className="whitespace-nowrap text-sm text-ink/60">
                <span className="font-semibold text-ink">
                  {rangeStart}–{rangeEnd}
                </span>{' '}
                of {total}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={page === 1 || limit === ALL_LIMIT}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="grid h-8 w-8 place-items-center rounded-lg border border-line bg-white text-ink/70 shadow-sm transition-colors hover:bg-cream disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={page >= totalPages || limit === ALL_LIMIT}
                  onClick={() => setPage((p) => p + 1)}
                  className="grid h-8 w-8 place-items-center rounded-lg border border-line bg-white text-ink/70 shadow-sm transition-colors hover:bg-cream disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-line bg-white shadow-sm">
        <div
          className={cn(
            'overflow-x-auto transition-opacity',
            isFetching && !isLoading && 'opacity-60',
          )}
        >
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-line bg-cream/60">
                {columns.map((col) => (
                  <th key={col} className={TH}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">{renderBody()}</tbody>
          </table>
        </div>
      </div>

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this user?</DialogTitle>
            <DialogDescription>
              This permanently deletes{' '}
              <span className="font-semibold text-ink">{deleteTarget?.name}</span> and every record
              tied to their account - leads, sessions, pincode assignments, and audit/email history.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={deleteUser.isPending}
              onClick={onConfirmDelete}
            >
              {deleteUser.isPending ? 'Deleting…' : 'Delete user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
